import { useCallback } from "react";
import { DotsThreeVerticalIcon, ShareFatIcon, TagIcon, FolderIcon } from "@phosphor-icons/react";

import { ICON_SIZE } from "@/config/constants";
import { NoteAction } from "@/features/notes/types";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { ShareNoteOption } from "@/features/notes/components/Note/ShareNoteOption";
import { SetNoteTagsOption } from "@/features/notes/components/Note/SetNoteTagsOption";
import { MoveNoteOption } from "@/features/notes/components/Note/MoveNoteOption";
import { NoteCardMoreActions } from "@/features/notes/components/Note/NoteCardMoreActions";

import { AddNote } from "@/services/api/fetchers/notes";

interface UseNotesActionsProps {
  userId: string | null;
  addNote: (note: AddNote) => void;
  tempDeleteNote: (id: number) => void;
  updateNote: (id: number, updates: Partial<FetchedNote>, shared: boolean) => void;
}

export function useNotesActions({ userId, addNote, tempDeleteNote, updateNote }: UseNotesActionsProps) {
  const actions = useCallback(
    (note: FetchedNote) => {
      const actions: NoteAction[] = [
        {
          key: "tags",
          label: "Tags",
          icon: <TagIcon size={ICON_SIZE.sm} />,
          content: () => <SetNoteTagsOption noteId={note.id} />,
        },
        {
          key: "move",
          label: "Move",
          icon: <FolderIcon size={ICON_SIZE.sm} />,
          content: (onClose) => <MoveNoteOption note={note} onClose={onClose} />,
        },
        {
          key: "share",
          label: "Share",
          icon: <ShareFatIcon size={ICON_SIZE.sm} weight={note.public_uuid ? "fill" : "regular"} />,
          content: () => <ShareNoteOption noteId={note.id} />,
        },
        {
          key: "more",
          label: "More",
          icon: <DotsThreeVerticalIcon size={ICON_SIZE.sm} weight="bold" />,
          content: (onClose) => (
            <NoteCardMoreActions
              note={note}
              userId={userId}
              onAddNote={addNote}
              onClose={onClose}
              onTempDeleteNote={tempDeleteNote}
              onUpdateNote={updateNote}
            />
          ),
        },
      ];

      return actions;
    },
    [userId, addNote, tempDeleteNote, updateNote]
  );

  return actions;
}
